/**
 * Upgrade rules: what you are allowed to buy, and what it does to the stats.
 *
 * Everything in here is a pure function of `(archetype, tiers, cash)`. No world
 * access, no clocks, no randomness. That is deliberate — the crossover rule is
 * the single most load-bearing piece of tower-defence design in the game, and
 * it needs to be provable rather than merely observed to work in the UI.
 *
 * ## The crossover rule (BTD6)
 *
 * Three paths, five tiers each, but you never get fifteen tiers:
 *
 * 1. At most **two** paths may be above tier 0.
 * 2. At most **one** path may be above tier 2.
 * 3. A path above tier 2 therefore caps the other two at tier 2.
 *
 * The three rules are not independent — 3 follows from 2 — but stating all
 * three is how players actually reason about it, so `maxTierFor` reports the
 * combined cap in one number and `purchaseCheck` names which rule bit.
 *
 * ## Effect folding order
 *
 * Deterministic and documented, because two orders give different numbers:
 *
 * 1. Start from a copy of `archetype.base`.
 * 2. **Additive pass** — walk path 0 tiers 1..k, then path 1, then path 2, and
 *    add every numeric field of `effects`. `damageType` is an assignment; the
 *    last node in that same walk order wins.
 * 3. **Multiplicative pass** — walk the same order again, take the product of
 *    every `effects.mul` entry per stat, and apply the products to the totals.
 * 4. **Clamp** — crit chance into 0..1, crit multiplier to at least 1, pierce
 *    to a non-negative integer, everything else to non-negative.
 *
 * Additive-then-multiplicative means a tier-5 multiplier scales the whole stack
 * underneath it, which is what makes a completed path feel like a payoff rather
 * than like one more increment.
 */

import { ECONOMY } from '@/core/config';
import type {
  Tower, TowerArchetype, TowerStats, UpgradeNode, UpgradePath, UpgradeTiers,
} from '@/core/types';
import { MAX_TIER, type NumericStat, type TowerEffects } from './Catalog';

/** Every numeric field of `TowerStats`, in a fixed order so folding is stable. */
export const NUMERIC_STATS: readonly NumericStat[] = [
  'damage', 'fireRate', 'range', 'pierce',
  'projectileSpeed', 'splash', 'armourPierce', 'critChance', 'critMultiplier',
];

/** Scratch multiplier accumulator. Module-scoped so folding allocates nothing. */
const mulAcc: Record<string, number> = Object.create(null);

// ───────────────────────────────────────────────────────────── purchasability ──

export type PurchaseFailure =
  | 'ok'
  /** Path already at tier 5. */
  | 'maxTier'
  /** Would open a third path. */
  | 'thirdPath'
  /** Another path is already above tier 2, so this one is capped there. */
  | 'crossover'
  /** Affordable in principle, not right now. */
  | 'cash';

const OTHERS: readonly [UpgradePath, UpgradePath][] = [[1, 2], [0, 2], [0, 1]];

/**
 * Highest tier `path` may ever reach given what the other two already hold.
 * Returns the current tier when the path is frozen where it stands.
 */
export function maxTierFor(tiers: UpgradeTiers, path: UpgradePath): number {
  const [a, b] = OTHERS[path];
  const opened = (tiers[0] > 0 ? 1 : 0) + (tiers[1] > 0 ? 1 : 0) + (tiers[2] > 0 ? 1 : 0);

  // Rule 1: a third path can never be opened.
  if (tiers[path] === 0 && opened >= 2) return 0;
  // Rule 2/3: somebody else already spent the one above-tier-2 slot.
  if (tiers[a] > 2 || tiers[b] > 2) return 2;
  return MAX_TIER;
}

/** The node a purchase on `path` would buy, or `null` if the path is exhausted. */
export function nextNode(archetype: TowerArchetype, tiers: UpgradeTiers, path: UpgradePath): UpgradeNode | null {
  const t = tiers[path];
  return t < MAX_TIER ? archetype.paths[path][t] : null;
}

/** Cost of the next node on `path`, or `Infinity` if there is not one. */
export function upgradeCost(archetype: TowerArchetype, tiers: UpgradeTiers, path: UpgradePath): number {
  const node = nextNode(archetype, tiers, path);
  return node ? node.cost : Infinity;
}

/** Full diagnosis, so the UI can say *why* a button is dead. */
export function purchaseCheck(tower: Tower, path: UpgradePath, cash: number): PurchaseFailure {
  const tiers = tower.tiers;
  if (tiers[path] >= MAX_TIER) return 'maxTier';

  const cap = maxTierFor(tiers, path);
  if (cap === 0) return 'thirdPath';
  if (tiers[path] >= cap) return 'crossover';

  const node = nextNode(tower.archetype, tiers, path);
  if (!node) return 'maxTier';
  return cash >= node.cost ? 'ok' : 'cash';
}

/** The one-line predicate the rest of the game calls. */
export function canPurchase(tower: Tower, path: UpgradePath, cash: number): boolean {
  return purchaseCheck(tower, path, cash) === 'ok';
}

/**
 * Applies a purchase. Mutates `tower` (tiers, stats, grants, invested) and
 * returns the cash spent, or 0 when the purchase was not legal. Callers deduct
 * the return value from `world.cash` — this function never touches the world.
 */
export function purchase(tower: Tower, path: UpgradePath, cash: number): number {
  if (!canPurchase(tower, path, cash)) return 0;
  const node = tower.archetype.paths[path][tower.tiers[path]];
  tower.tiers[path]++;
  tower.invested += node.cost;
  tower.stats = applyUpgrades(tower.archetype, tower.tiers);
  collectGrants(tower.archetype, tower.tiers, tower.grants);
  return node.cost;
}

// ────────────────────────────────────────────────────────────────── folding ──

/**
 * Folds `archetype.base` and every purchased node into a finished stat block.
 * Allocates exactly one object — the result — so it is safe to call on every
 * purchase but should not be called inside the sim loop.
 */
export function applyUpgrades(archetype: TowerArchetype, tiers: UpgradeTiers): TowerStats {
  const out: TowerStats = {
    damage: archetype.base.damage,
    damageType: archetype.base.damageType,
    fireRate: archetype.base.fireRate,
    range: archetype.base.range,
    pierce: archetype.base.pierce,
    projectileSpeed: archetype.base.projectileSpeed,
    splash: archetype.base.splash,
    armourPierce: archetype.base.armourPierce,
    critChance: archetype.base.critChance,
    critMultiplier: archetype.base.critMultiplier,
  };

  for (let s = 0; s < NUMERIC_STATS.length; s++) mulAcc[NUMERIC_STATS[s]] = 1;

  // Pass 1 — additive, path 0 then 1 then 2, tier 1 upward inside each path.
  for (let p = 0 as UpgradePath; p < 3; p = (p + 1) as UpgradePath) {
    const nodes = archetype.paths[p];
    const top = Math.min(tiers[p], nodes.length);
    for (let t = 0; t < top; t++) {
      const e = nodes[t].effects as TowerEffects;
      for (let s = 0; s < NUMERIC_STATS.length; s++) {
        const key = NUMERIC_STATS[s];
        const v = e[key];
        if (typeof v === 'number') out[key] += v;
      }
      if (e.damageType) out.damageType = e.damageType;
      if (e.mul) {
        for (let s = 0; s < NUMERIC_STATS.length; s++) {
          const key = NUMERIC_STATS[s];
          const m = e.mul[key];
          if (typeof m === 'number') mulAcc[key] *= m;
        }
      }
    }
  }

  // Pass 2 — multiplicative, applied to the fully-added totals.
  for (let s = 0; s < NUMERIC_STATS.length; s++) {
    const key = NUMERIC_STATS[s];
    const m = mulAcc[key];
    if (m !== 1) out[key] *= m;
  }

  // Pass 3 — clamps.
  if (out.damage < 0) out.damage = 0;
  if (out.fireRate < 0) out.fireRate = 0;
  if (out.range < 0) out.range = 0;
  out.pierce = Math.max(0, Math.round(out.pierce));
  if (out.projectileSpeed < 0) out.projectileSpeed = 0;
  if (out.splash < 0) out.splash = 0;
  if (out.armourPierce < 0) out.armourPierce = 0;
  out.critChance = out.critChance < 0 ? 0 : out.critChance > 1 ? 1 : out.critChance;
  if (out.critMultiplier < 1) out.critMultiplier = 1;

  return out;
}

/**
 * Rebuilds the ability set for a tier configuration. Clears `into` first so the
 * function is idempotent and a sell-and-rebuild cannot leak stale grants.
 * Later nodes override earlier ones for the same verb — see `grantNumber`.
 */
export function collectGrants(archetype: TowerArchetype, tiers: UpgradeTiers, into: Set<string>): Set<string> {
  into.clear();
  for (let p = 0 as UpgradePath; p < 3; p = (p + 1) as UpgradePath) {
    const nodes = archetype.paths[p];
    const top = Math.min(tiers[p], nodes.length);
    for (let t = 0; t < top; t++) {
      const g = (nodes[t].effects as TowerEffects).grants;
      if (!g) continue;
      for (let i = 0; i < g.length; i++) into.add(g[i]);
    }
  }
  return into;
}

// ──────────────────────────────────────────────────────────── grant parsing ──

/**
 * Reads the numeric argument of the strongest `verb:...` grant in a set.
 *
 * A tier-3 node adding `chain:7` does not remove the tier-2 node's `chain:4`,
 * so the set holds both and the winner is decided here by magnitude. Verbs
 * whose "better" direction is downward (`slow`, `aura:slow`) pass `lowest`.
 */
export function grantNumber(grants: Set<string>, verb: string, fallback: number, lowest = false): number {
  let best = fallback;
  let found = false;
  const prefix = verb + ':';
  for (const g of grants) {
    if (!g.startsWith(prefix)) continue;
    const rest = g.slice(prefix.length);
    const cut = rest.indexOf(':');
    const v = parseFloat(cut < 0 ? rest : rest.slice(0, cut));
    if (!Number.isFinite(v)) continue;
    if (!found) { best = v; found = true; continue; }
    best = lowest ? Math.min(best, v) : Math.max(best, v);
  }
  return best;
}

/**
 * Reads the `index`-th argument of the `verb:` grant whose *first* argument is
 * largest. Keeps multi-argument grants like `napalm:DPS:SEC:RADIUS` internally
 * consistent instead of mixing the DPS of one tier with the radius of another.
 */
export function grantArg(grants: Set<string>, verb: string, index: number, fallback: number): number {
  let bestLead = -Infinity;
  let out = fallback;
  const prefix = verb + ':';
  for (const g of grants) {
    if (!g.startsWith(prefix)) continue;
    const parts = g.split(':');
    const lead = parseFloat(parts[1]);
    if (!Number.isFinite(lead) || lead <= bestLead) continue;
    bestLead = lead;
    const v = parseFloat(parts[1 + index]);
    out = Number.isFinite(v) ? v : fallback;
  }
  return out;
}

export function hasGrant(grants: Set<string>, verb: string): boolean {
  if (grants.has(verb)) return true;
  const prefix = verb + ':';
  for (const g of grants) if (g.startsWith(prefix)) return true;
  return false;
}

// ──────────────────────────────────────────────────────────────── economics ──

/** Cash sunk into a configuration: the tower plus every node bought. */
export function totalInvested(archetype: TowerArchetype, tiers: UpgradeTiers): number {
  let sum = archetype.cost;
  for (let p = 0 as UpgradePath; p < 3; p = (p + 1) as UpgradePath) {
    const nodes = archetype.paths[p];
    const top = Math.min(tiers[p], nodes.length);
    for (let t = 0; t < top; t++) sum += nodes[t].cost;
  }
  return sum;
}

/** Refund for selling. Floored, so selling never rounds in the player's favour. */
export function sellValue(tower: Tower): number {
  return Math.floor(tower.invested * ECONOMY.sellRatio);
}
